/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useMediaQuery } from '@/hooks/use-media-query';

// ============================================================================
// Types
// ============================================================================

/**
 * Background type options for the serene experience
 */
export type SereneBackgroundType = 'gradient' | 'particles' | 'wave';

/**
 * Available breathing/calming techniques
 */
export type SereneTechnique = '478' | 'box' | 'grounding' | null;

/**
 * User preferences for the serene experience
 */
export interface SerenePreferences {
  /** Whether ambient audio is enabled */
  audioEnabled: boolean;
  /** Type of background visual effect */
  backgroundType: SereneBackgroundType;
  /** Currently selected calming technique, null if none selected */
  selectedTechnique: SereneTechnique;
}

/**
 * Session state for the serene experience (not persisted to localStorage)
 *
 * NOTE: This interface is intentionally pre-built infrastructure for planned features:
 * - Session analytics (track active session duration)
 * - Auto-pause on tab blur
 * - Session restoration on unmount/remount
 *
 * Currently not consumed but retained to avoid re-implementing when features ship.
 */
export interface SereneSessionState {
  /** Whether an exercise session is currently active */
  sessionActive: boolean;
}

/**
 * Persisted state for onboarding and technique tracking
 */
export interface SereneOnboardingState {
  /** Whether user has completed or skipped onboarding */
  onboardingComplete: boolean;
  /** Last-used breathing/calming technique */
  lastTechnique: SereneTechnique;
}

/**
 * Serene context value exposed to consumers
 */
export interface SereneContextValue {
  /** Current user preferences */
  preferences: SerenePreferences;
  /** Update a specific preference */
  updatePreference: <K extends keyof SerenePreferences>(
    key: K,
    value: SerenePreferences[K]
  ) => void;
  /** Reset all preferences to defaults */
  resetPreferences: () => void;
  /** Whether reduced motion is preferred (system accessibility setting) */
  prefersReducedMotion: boolean;

  // Onboarding state
  /** Whether user has completed or skipped onboarding */
  onboardingComplete: boolean;
  /** Set onboarding completion status (persisted to localStorage) */
  setOnboardingComplete: (complete: boolean) => void;

  // Last technique state
  /** Last-used breathing/calming technique */
  lastTechnique: SereneTechnique;
  /** Set last-used technique (persisted to localStorage) */
  setLastTechnique: (technique: SereneTechnique) => void;

  // Session state
  /** Whether an exercise session is currently active */
  sessionActive: boolean;
  /** Set session active state (not persisted - ephemeral) */
  setSessionActive: (active: boolean) => void;
}

/**
 * Props for the SereneProvider component
 */
export interface SereneProviderProps {
  children: ReactNode;
  /** Optional initial preferences (primarily for testing) */
  defaultPreferences?: Partial<SerenePreferences>;
  /** Optional initial onboarding state (primarily for testing) */
  defaultOnboardingState?: Partial<SereneOnboardingState>;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * localStorage key for persisting serene preferences
 */
const SERENE_STORAGE_KEY = 'serene-preferences';

/**
 * localStorage key for persisting onboarding completion status
 */
const SERENE_ONBOARDING_KEY = 'serene-onboarding-complete';

/**
 * localStorage key for persisting last-used technique
 */
const SERENE_LAST_TECHNIQUE_KEY = 'serene-last-technique';

/**
 * Default preferences for serene experience
 * These provide a sensible starting point without any user configuration
 */
const DEFAULT_PREFERENCES: SerenePreferences = {
  audioEnabled: false, // Audio off by default for accessibility
  backgroundType: 'gradient', // Gentle gradient as default visual
  selectedTechnique: null, // No technique pre-selected
};

/**
 * Default onboarding state
 */
const DEFAULT_ONBOARDING_STATE: SereneOnboardingState = {
  onboardingComplete: false, // Show onboarding by default
  lastTechnique: null, // No technique used yet
};

/**
 * Preferences optimized for users who prefer reduced motion
 * Disables animations and particle effects
 */
const REDUCED_MOTION_PREFERENCES: Partial<SerenePreferences> = {
  backgroundType: 'gradient', // Static gradient instead of animated particles
};

// ============================================================================
// Utilities
// ============================================================================

/**
 * Safely get initial preferences from localStorage
 * Falls back to defaults if localStorage is unavailable or contains invalid data
 *
 * Note: This function is called in useState initializer, so it should be pure
 * and not depend on React hooks. The prefersReducedMotion check is done separately
 * in the provider component.
 */
const getStoredPreferences = (
  defaultPrefs?: Partial<SerenePreferences>
): SerenePreferences => {
  // Start with base defaults
  let basePreferences = { ...DEFAULT_PREFERENCES };

  // Apply any provided default overrides
  if (defaultPrefs) {
    basePreferences = { ...basePreferences, ...defaultPrefs };
  }

  // Guard against SSR
  if (typeof window === 'undefined') {
    return basePreferences;
  }

  try {
    const stored = window.localStorage.getItem(SERENE_STORAGE_KEY);
    if (stored === null) {
      return basePreferences;
    }

    const parsed = JSON.parse(stored);

    // Validate the parsed object has expected shape
    if (typeof parsed !== 'object' || parsed === null) {
      return basePreferences;
    }

    // Merge stored preferences with defaults (stored values take precedence)
    // This ensures new preference keys get default values
    return {
      ...basePreferences,
      ...parsed,
    };
  } catch {
    // Invalid JSON or localStorage error - use defaults
    return basePreferences;
  }
};

/**
 * Safely persist preferences to localStorage
 */
const persistPreferences = (preferences: SerenePreferences): void => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(
      SERENE_STORAGE_KEY,
      JSON.stringify(preferences)
    );
  } catch {
    console.warn('Failed to persist serene preferences to localStorage');
  }
};

/**
 * Safely get onboarding state from localStorage
 * Falls back to defaults if localStorage is unavailable or contains invalid data
 */
const getStoredOnboardingState = (
  defaultState?: Partial<SereneOnboardingState>
): SereneOnboardingState => {
  // Start with base defaults
  let baseState = { ...DEFAULT_ONBOARDING_STATE };

  // Apply any provided default overrides
  if (defaultState) {
    baseState = { ...baseState, ...defaultState };
  }

  // Guard against SSR
  if (typeof window === 'undefined') {
    return baseState;
  }

  try {
    // Get onboarding completion status
    const storedOnboarding = window.localStorage.getItem(SERENE_ONBOARDING_KEY);
    if (storedOnboarding !== null) {
      baseState.onboardingComplete = storedOnboarding === 'true';
    }

    // Get last technique
    const storedTechnique = window.localStorage.getItem(SERENE_LAST_TECHNIQUE_KEY);
    if (storedTechnique !== null) {
      // Validate the stored technique is a valid value
      const validTechniques: SereneTechnique[] = ['478', 'box', 'grounding', null];
      const parsedTechnique = storedTechnique === 'null' ? null : storedTechnique;
      if (validTechniques.includes(parsedTechnique as SereneTechnique)) {
        baseState.lastTechnique = parsedTechnique as SereneTechnique;
      }
    }

    return baseState;
  } catch {
    // localStorage error - use defaults
    return baseState;
  }
};

/**
 * Safely persist onboarding completion to localStorage
 */
const persistOnboardingComplete = (complete: boolean): void => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(SERENE_ONBOARDING_KEY, String(complete));
  } catch {
    console.warn('Failed to persist serene onboarding state to localStorage');
  }
};

/**
 * Safely persist last technique to localStorage
 */
const persistLastTechnique = (technique: SereneTechnique): void => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(SERENE_LAST_TECHNIQUE_KEY, String(technique));
  } catch {
    console.warn('Failed to persist serene last technique to localStorage');
  }
};

// ============================================================================
// Context
// ============================================================================

/**
 * Context for serene experience state
 * Initialized with null to detect missing provider
 */
const SereneContext = createContext<SereneContextValue | null>(null);
SereneContext.displayName = 'SereneContext';

// ============================================================================
// Provider Component
// ============================================================================

/**
 * SereneProvider - Provides shared serene experience preferences to components
 *
 * This provider manages user preferences for the calming/serene experience,
 * including audio settings, background visuals, dim mode, and technique selection.
 *
 * Features:
 * - SSR-safe localStorage initialization and persistence
 * - Automatic state persistence on preference changes
 * - Integration with prefers-reduced-motion accessibility setting
 * - Stable callback references (memoized)
 * - Default values that work without any user configuration
 *
 * @example
 * ```tsx
 * // In app provider tree
 * <SereneProvider>
 *   <SerenePage />
 * </SereneProvider>
 *
 * // In components
 * const { preferences, updatePreference } = useSerenePreferences();
 * ```
 */
export function SereneProvider({
  children,
  defaultPreferences,
  defaultOnboardingState,
}: SereneProviderProps) {
  // Check system preference for reduced motion
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  // Initialize preferences from localStorage with SSR safety
  // Note: prefersReducedMotion is not available during initial render (returns false)
  // so we initialize from localStorage only, then handle reduced motion separately
  const [preferences, setPreferences] = useState<SerenePreferences>(() =>
    getStoredPreferences(defaultPreferences)
  );

  // Initialize onboarding state from localStorage
  const [onboardingState, setOnboardingState] = useState<SereneOnboardingState>(
    () => getStoredOnboardingState(defaultOnboardingState)
  );

  // Session state is ephemeral (not persisted)
  const [sessionActive, setSessionActiveState] = useState(false);

  // Sync preferences to localStorage
  useEffect(() => {
    persistPreferences(preferences);
  }, [preferences]);

  // Sync onboarding completion to localStorage
  useEffect(() => {
    persistOnboardingComplete(onboardingState.onboardingComplete);
  }, [onboardingState.onboardingComplete]);

  // Sync last technique to localStorage
  useEffect(() => {
    persistLastTechnique(onboardingState.lastTechnique);
  }, [onboardingState.lastTechnique]);

  // Update a specific preference
  const updatePreference = useCallback(
    <K extends keyof SerenePreferences>(key: K, value: SerenePreferences[K]) => {
      setPreferences((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  // Reset all preferences to defaults
  const resetPreferences = useCallback(() => {
    const defaults = prefersReducedMotion
      ? { ...DEFAULT_PREFERENCES, ...REDUCED_MOTION_PREFERENCES }
      : DEFAULT_PREFERENCES;
    setPreferences(defaults);
  }, [prefersReducedMotion]);

  // Onboarding state setters
  const setOnboardingComplete = useCallback((complete: boolean) => {
    setOnboardingState((prev) => ({ ...prev, onboardingComplete: complete }));
  }, []);

  const setLastTechnique = useCallback((technique: SereneTechnique) => {
    setOnboardingState((prev) => ({ ...prev, lastTechnique: technique }));
  }, []);

  // Session state setter (stable reference)
  const setSessionActive = useCallback((active: boolean) => {
    setSessionActiveState(active);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<SereneContextValue>(
    () => ({
      preferences,
      updatePreference,
      resetPreferences,
      prefersReducedMotion,
      // Onboarding state
      onboardingComplete: onboardingState.onboardingComplete,
      setOnboardingComplete,
      // Last technique state
      lastTechnique: onboardingState.lastTechnique,
      setLastTechnique,
      // Session state
      sessionActive,
      setSessionActive,
    }),
    [
      preferences,
      updatePreference,
      resetPreferences,
      prefersReducedMotion,
      onboardingState.onboardingComplete,
      setOnboardingComplete,
      onboardingState.lastTechnique,
      setLastTechnique,
      sessionActive,
      setSessionActive,
    ]
  );

  return (
    <SereneContext.Provider value={contextValue}>
      {children}
    </SereneContext.Provider>
  );
}
SereneProvider.displayName = 'SereneProvider';

// ============================================================================
// Consumer Hooks
// ============================================================================

/**
 * useSerenePreferences - Hook for consuming serene preferences context
 *
 * Must be used within a SereneProvider. Throws an error if used outside
 * the provider tree.
 *
 * @returns Serene preferences state and control functions
 * @throws Error if used outside SereneProvider
 *
 * @example
 * ```tsx
 * const { preferences, updatePreference, resetPreferences } = useSerenePreferences();
 *
 * // Update a single preference
 * updatePreference('audioEnabled', true);
 *
 * // Access current preferences
 * if (preferences.backgroundType === 'particles') {
 *   // Apply particle effects
 * }
 * ```
 */
export function useSerenePreferences(): SereneContextValue {
  const context = useContext(SereneContext);

  if (!context) {
    throw new Error(
      'useSerenePreferences must be used within a SereneProvider. ' +
        'Wrap your serene routes with <SereneProvider>.'
    );
  }

  return context;
}

/**
 * useIsSereneMode - Hook for detecting if component is within serene context
 *
 * Use this in components that may or may not be within the SereneProvider.
 * Returns true if the component is within the serene provider context.
 *
 * @returns true if within SereneProvider, false otherwise
 *
 * @example
 * ```tsx
 * const isInSereneMode = useIsSereneMode();
 *
 * if (isInSereneMode) {
 *   // Apply serene-specific behavior
 * }
 * ```
 */
export function useIsSereneMode(): boolean {
  const context = useContext(SereneContext);
  return context !== null;
}

/**
 * useSerenePreferencesOptional - Optional hook that returns null when outside SereneProvider
 *
 * Use this in components that need to adapt behavior based on whether
 * they're in a serene context, without throwing errors.
 *
 * @returns SereneContextValue if within provider, null otherwise
 *
 * @example
 * ```tsx
 * const serene = useSerenePreferencesOptional();
 * const backgroundType = serene?.preferences.backgroundType ?? 'gradient';
 * ```
 */
export function useSerenePreferencesOptional(): SereneContextValue | null {
  return useContext(SereneContext);
}
